// Administradores do painel: leitura junta `user_roles` (role admin) com
// `profiles`; criação/remoção passam pela edge function `manage-admin`
// (precisa de service role para mexer em auth.users). `functions.invoke`
// já envia o JWT da sessão — sem `getSession` + `fetch` manual.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AdminUser {
  user_id: string;
  name: string;
  email: string;
  avatar_url: string | null | undefined;
  created_at: string;
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin-users"],
    queryFn: async (): Promise<AdminUser[]> => {
      const { data: roles, error } = await supabase
        .from("user_roles")
        .select("user_id, role, created_at")
        .eq("role", "admin");
      if (error) throw error;

      const userIds = roles?.map((r) => r.user_id) ?? [];
      if (userIds.length === 0) return [];

      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, name, email, avatar_url")
        .in("id", userIds);
      if (profilesError) throw profilesError;

      return (roles ?? []).map((role) => {
        const profile = profiles?.find((p) => p.id === role.user_id);
        return {
          user_id: role.user_id,
          name: profile?.name || "Sem nome",
          email: profile?.email || "",
          avatar_url: profile?.avatar_url,
          created_at: role.created_at,
        };
      });
    },
  });
}

/** Erros da função vêm como `{ error }` no body; `invoke` só lança em falha de rede/HTTP. */
async function unwrap<T>(res: { data: T | null; error: unknown }, fallback: string): Promise<T> {
  if (res.error) {
    const e = res.error as { message?: string; context?: Response };
    // FunctionsHttpError: the JSON body carries the real reason.
    const body = e.context ? await e.context.json().catch(() => null) : null;
    throw new Error(body?.error || e.message || fallback);
  }
  const data = res.data as (T & { error?: string }) | null;
  if (data && typeof data === "object" && "error" in data && data.error)
    throw new Error(data.error);
  return data as T;
}

export function useCreateAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; password: string; name?: string }) => {
      const res = await supabase.functions.invoke<{ ok?: boolean; error?: string }>(
        "manage-admin",
        {
          method: "POST",
          body: input,
        },
      );
      return unwrap(res, "Erro ao criar administrador");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
}

export function useRemoveAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await supabase.functions.invoke<{ ok?: boolean; error?: string }>(
        "manage-admin",
        {
          method: "DELETE",
          body: { user_id: userId },
        },
      );
      return unwrap(res, "Erro ao remover administrador");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users"] }),
  });
}
